// applyNodeSearchQuery.js

import { getNodeSubtitle } from 'utilities/NodeSubtitle';
import { normalizeSearchText, tokenizeSearchQuery } from 'services/search/SearchIndexService';
import { extractRichTextPlainText } from 'utilities/RichText';

/**
 * Returns schema fields included in toolbar text search.
 *
 * @param {Array} resolvedProperties
 * @returns {Array}
 */
export const getSearchableSchemaFields = (resolvedProperties) => {
    return (Array.isArray(resolvedProperties) ? resolvedProperties : []).filter((field) => {
        const type = String(field?.type || '').trim().toLowerCase();
        return type === 'string' || type === 'enum' || type === 'richtext';
    });
};

/**
 * Filters node rows by a toolbar search query across title, tags, and searchable schema fields.
 *
 * @param {Array} items
 * @param {string} searchQuery
 * @param {object} params
 * @param {function} params.resolveFieldValue
 * @param {Array} params.searchableSchemaFields
 * @returns {Array}
 */
export default function applyNodeSearchQuery(items, searchQuery, {
    resolveFieldValue,
    searchableSchemaFields = [],
}) {
    const baseItems = Array.isArray(items) ? items : [];
    const searchTokens = tokenizeSearchQuery(searchQuery);

    if (!searchTokens.length) return baseItems;

    return baseItems.filter((node) => {
        const texts = [
            node?.title,
            getNodeSubtitle(node),
            Array.isArray(node?.tags) ? node.tags.join(' ') : '',
        ];

        searchableSchemaFields.forEach((fieldDef) => {
            const rawValue = resolveFieldValue?.(node, fieldDef);
            if (rawValue == null) return;

            if (String(fieldDef?.type || '').trim().toLowerCase() === 'richtext') {
                texts.push(extractRichTextPlainText(rawValue));
                return;
            }

            if (Array.isArray(rawValue)) {
                texts.push(rawValue.join(' '));
                return;
            }

            texts.push(String(rawValue));
        });

        const haystack = normalizeSearchText(texts.join(' '));
        if (!haystack) return false;

        return searchTokens.every((token) => haystack.includes(token));
    });
}
